import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import Card, { CARD_HOVER_SX, CARD_HOVER_TRANSITION, CARD_TITLE_CLASS } from "@plannel/components/dashboard/DashboardCard";
import { SegToggle } from "@plannel/components/dashboard/DashboardToggle";
import { fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

/*
 * DW 대시보드 공용 KPI 카드.
 *
 *  KpiCard      — 범용. 이미 포맷된 value 문자열을 받는다. %/rate/일수 등 어떤 지표든 가능.
 *  KpiSalesCard — 금액/수량(AMT·QTY) 전용 특수화. KpiCard 위에 조립.
 *
 * 금액/수량이 아닌 지표(달성률·충족률 등)는 KpiSalesCard 대신 KpiCard 를 쓴다.
 */

// KPI 카드 sparkline — data: [{ i, v }]. 그라데이션 영역.
//   색은 테마 primary 가 아니라 사용자 팔레트(Personal Settings)의 첫 색 — 카드 안의 다른 차트와 같은 규칙.
//   color 를 주면 그 색을 쓴다(기본값은 종전과 동일). 모듈에 속한 지표를 그릴 때 **그 모듈 색**을
//   넘기라고 있는 문이다 — 앱 규칙이 "색이 곧 권한 소속"이라, RP 지표를 colorSet(0)(=BF 색)으로
//   그리면 같은 화면의 모듈 표시와 어긋난다.
export const Sparkline = ({ gradientId, data, color }) => {
  const primary = color || ColorUtil.colorSet(0);
  return (
    <Box sx={{ width: 120, height: 42, flexShrink: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={primary} stopOpacity={0.25} />
              <stop offset="100%" stopColor={primary} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={primary} strokeWidth={2}
            fill={`url(#${gradientId})`} dot={false} activeDot={false} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

/**
 * 범용 KPI 카드.
 * @param label     카드 제목
 * @param value     **이미 포맷된** 표시 문자열 (예: "¥1.3T", "92.4%", "12일")
 * @param deltaPct  증감률(숫자). null/undefined 면 델타 영역 자체를 숨김
 * @param deltaUnit 델타 뒤에 붙일 단위. 기본 "%".
 *                  ★ 값 자체가 이미 비율인 KPI(정확도·편향 등)는 "pp"(퍼센트 포인트)를 넘긴다 —
 *                    비율의 증감을 다시 % 로 쓰면 읽는 사람이 반드시 오해한다.
 * @param deltaGood 이번 변화가 **좋은 것인지**(색·화살표 방향). 생략하면 부호로 판단(올라가면 좋다).
 *                  ★ "올라가면 좋다" 가 안 통하는 지표에 쓴다 — 대표적으로 **편향**은 0 에 가까울수록
 *                    좋아서, +10% → +40% 는 델타가 양수지만 실제로는 나빠진 것이다.
 * @param sub       값 아래 보조 캡션
 * @param right     제목 우측 컨트롤(토글 등)
 * @param sparkData [{ i, v }] — 없으면 스파크라인 생략
 * @param onClick   주면 카드 전체가 클릭 가능(드릴스루) — cursor + hover 어포던스 자동
 */
export const KpiCard = ({ label, value, deltaPct, deltaUnit = "%", deltaGood, sub, right, sparkData, gradientId, onClick, sx, leading }) => {
  const hasDelta = deltaPct != null;
  const isUp = (deltaPct ?? 0) >= 0;
  // 색·화살표는 "좋아졌나"를 나타낸다. deltaGood 을 안 주면 부호로 판단한다(올라가면 좋다).
  const good = deltaGood ?? isUp;
  return (
    <Card
      onClick={onClick}
      sx={{
        flex: 1,
        ...(onClick && {
          cursor: "pointer",
          transition: CARD_HOVER_TRANSITION,
          "&:hover": CARD_HOVER_SX,
        }),
        ...sx,
      }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {leading}
          {/* 클릭 가능한 카드면 hover 시 제목도 파랗게 — CARD_HOVER_SX 가 이 클래스를 잡는다 */}
          <Typography className={CARD_TITLE_CLASS} sx={{ fontSize: FS.TITLE, fontWeight: 600, color: "text.primary", transition: "color 0.15s" }}>{label}</Typography>
        </Box>
        {right}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
            <Typography sx={{ fontSize: FS.KPI, fontWeight: 700, color: "text.primary" }}>{value}</Typography>
            {hasDelta && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                {/* 부호(+/−)는 값의 변화 방향, 색·화살표는 그게 좋은 변화인지 — 둘은 다를 수 있다 */}
                <Typography sx={{ fontSize: FS.H2, color: good ? "success.main" : "warning.main", fontWeight: 600 }}>
                  {isUp ? "+" : "-"}{Math.abs(deltaPct)}{deltaUnit}
                </Typography>
                {good
                  ? <ArrowDropUp sx={{ fontSize: 21, color: "success.main" }} />
                  : <ArrowDropDown sx={{ fontSize: 21, color: "warning.main" }} />}
              </Box>
            )}
          </Box>
          {sub && <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", mt: 0.5 }}>{sub}</Typography>}
        </Box>
        {sparkData && <Sparkline gradientId={gradientId} data={sparkData} />}
      </Box>
    </Card>
  );
};

/**
 * 금액/수량 KPI 카드 — kpi { amt, qty, deltaPct }.
 *   fmtMoney : 호출측이 makeFmtMoney(currencyCd) 로 만든 금액 포맷터
 *   hasQty=false → AMT 만(수량 토글 없음)
 *   unit prop 을 주면 제어 모드(외부 통합 토글 사용) → 카드 내 토글 숨김. 없으면 카드가 자체 관리.
 *   onClick 을 주면 카드 전체 클릭(드릴스루).
 */
export const KpiSalesCard = ({ label, sub, kpi, fmtMoney, sparkData, gradientId, hasQty = true, unit: unitProp, onClick }) => {
  const [unitState, setUnitState] = useState("AMT");
  const unit = unitProp ?? unitState;
  const value = unit === "AMT" || !hasQty ? fmtMoney(kpi?.amt ?? 0) : fmtQty(kpi?.qty ?? 0);
  return (
    <KpiCard
      label={label}
      value={value}
      deltaPct={kpi?.deltaPct ?? 0}
      sub={sub}
      sparkData={sparkData}
      gradientId={gradientId}
      onClick={onClick}
      right={hasQty && unitProp == null
        ? <SegToggle value={unit} options={["AMT", "QTY"]} onChange={setUnitState} />
        : null}
    />
  );
};
